import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { differenceInDays } from 'date-fns';
import queryString from 'query-string';
import {
  MenuItems,
  Response,
  Pagination,
  ValidatorsProps,
  ChangeLog,
} from '@ui/src';
import { accountsCategoriesApi } from '../../charts/store/services/categories';
import { accountsListApi } from './services';
import { Category } from '../../charts/types';
import { Account, AccountsStats, AccountsValidatorResponse } from '../types';
import { Balance } from '../../statements/types';

import ROUTES from '../../../routes';

export interface AccounstListState {
  isLoading: boolean;
  accounts?: Account[];
  account?: Account;
  menuItems?: MenuItems[];
  menuItemsLoaded?: boolean;
  pagination?: Pagination;
  validators?: {
    createAccountSchema: ValidatorsProps;
  };
  balances?: Balance[];
  accountsStateInititate?: boolean;
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
}

const initialState: AccounstListState = {
  isLoading: false,
  accounts: undefined,
  menuItems: undefined,
  menuItemsLoaded: false,
  balances: undefined,
  accountsStateInititate: false,
};

export const accountsListSlice = createSlice({
  name: 'accountsList',
  initialState,
  reducers: {
    clearAccountsState: (state) => {
      state.menuItems = undefined;
      state.accounts = undefined;
    },
    setAccountsStateInitiate: (state, action: PayloadAction<boolean>) => {
      state.accountsStateInititate = action.payload;
      state.menuItemsLoaded = action.payload;
    },
    clearAccoutState: (state) => {
      state.account = undefined;
      state.balances = undefined;
      state.changeLogsPagination = undefined;
      state.changeLogs = undefined;
    },
    clearAccountsClosingBalances: (state) => {
      state.accounts = state.accounts?.map((item) => {
        const result = { ...item };

        result.closingBalances = undefined;

        return result;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      accountsListApi.endpoints.getAccountsValidators.matchFulfilled,
      (state, action: PayloadAction<AccountsValidatorResponse>) => {
        state.validators = {
          createAccountSchema: action.payload.data.createAccountSchema,
        };
      },
    );
    builder
      .addMatcher(
        accountsCategoriesApi.endpoints.getAccountsCategories.matchPending,
        (state) => {
          state.isLoading = true;
          state.menuItems = undefined;
        },
      )
      .addMatcher(
        accountsListApi.endpoints.changeAccountBalanceStatus.matchFulfilled,
        (state, action: any & PayloadAction<{ data: Balance }>) => {
          if (state.balances) {
            let nextBalances = ([] as Balance[]).concat(state.balances);

            if (action.meta.arg.originalArgs.body.active) {
              nextBalances.push(action.payload.data);
            } else if (
              nextBalances.find((item) => item.id === action.payload.data.id)
            ) {
              nextBalances = nextBalances.filter(
                (item) => item.id !== action.payload.data.id,
              );
            }

            state.balances = nextBalances;
          }
        },
      )
      .addMatcher(
        accountsCategoriesApi.endpoints.getAccountsCategories.matchFulfilled,
        (state, action: PayloadAction<Response<Category>>) => {
          const nextMenuItems = action.payload.data.edges.map((item) => ({
            category: {
              label: item.node.name,
              counter: '',
              id: item.node.id,
              type: item.node.type,
              number: item.node.number,
              groups: item.node.accountGroups?.map((group) => ({
                label: group.description,
                counter: '',
                id: group.id,
                number: group.number,
                accounts: group.primaryAccounts?.map((account) => ({
                  label: account.description,
                  number: account.number,
                  counter: '',
                  id: account.id,
                })),
              })),
            },
          }));

          state.menuItems = nextMenuItems;
        },
      )
      .addMatcher(
        accountsListApi.endpoints.getAccountsStats.matchFulfilled,
        (state, action: PayloadAction<{ data: AccountsStats[] }>) => {
          state.accountsStateInititate = true;

          const nextMenuItems = state.menuItems?.map((menuItem) => {
            const result = { ...menuItem };
            const currentCategory = action.payload.data?.find(
              (item) => item.id === menuItem.category.id,
            );

            if (currentCategory) {
              result.category.counter = currentCategory.count.toString();

              result.category.groups = result.category.groups?.map((group) => {
                const groupResult = { ...group };
                const currentGroup = currentCategory.accountGroups.find(
                  (item) => item.id === group.id,
                );

                if (currentGroup) {
                  groupResult.counter = currentGroup.count.toString();

                  groupResult.accounts = groupResult.accounts?.map(
                    (primary) => {
                      const accountsResult = { ...primary };
                      const currentAccount = currentGroup.primaryAccounts.find(
                        (item) => item.id === primary.id,
                      );

                      if (currentAccount) {
                        accountsResult.counter =
                          currentAccount.count.toString();
                      }

                      return accountsResult;
                    },
                  );
                }

                return groupResult;
              });
            }

            return result;
          });

          state.menuItems = nextMenuItems;
          state.isLoading = false;
          state.menuItemsLoaded = true;
        },
      )
      .addMatcher(
        accountsListApi.endpoints.getAccountsList.matchFulfilled,
        (state, action: PayloadAction<Response<Account>>) => {
          if (state.accountsStateInititate) {
            state.accounts = action.payload.data.edges.map((item) => ({
              ...item.node,
              search: '',
              url: generatePath(ROUTES.ACCOUNTS_EDIT, {
                accountId: item.node.id,
              }),
            }));

            state.pagination = action.payload.data.pageInfo;
          }
        },
      )
      .addMatcher(
        accountsListApi.endpoints.getAccountChangeLogs.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<ChangeLog>> & {
            meta: { arg: { originalArgs: string } };
          },
        ) => {
          if (!action.meta.arg.originalArgs.includes('cursor')) {
            state.changeLogs = action.payload.data?.edges?.map(
              (item) => item.node,
            );
          } else {
            const nextChangeLogs = ([] as ChangeLog[]).concat(
              state.changeLogs as ChangeLog[],
            );

            action.payload.data.edges?.forEach((item) => {
              nextChangeLogs.push(item.node);
            });

            state.changeLogs = nextChangeLogs;
          }

          state.changeLogsPagination = action.payload.data?.pageInfo;
        },
      )
      .addMatcher(
        accountsListApi.endpoints.getAccount.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          state.account = action.payload.data;
        },
      )
      .addMatcher(
        accountsListApi.endpoints.getAccountsBalances.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: Balance[] }> & {
            meta: { arg: { originalArgs: string } };
          },
        ) => {
          const parsedQuery = queryString.parse(action.meta.arg.originalArgs);

          const daysDiff = differenceInDays(
            new Date(),
            new Date(parsedQuery.date as string),
          );

          state.accounts = state.accounts?.map((item) => {
            const result = { ...item };

            const currentBalances = action?.payload?.data?.filter(
              (balance) => balance.accountId === item.id,
            );

            if (currentBalances) {
              if (daysDiff === 0) {
                result.balances = currentBalances;
              }

              if (daysDiff > 0) {
                result.closingBalances = currentBalances;
              }
            }

            return result;
          });
        },
      )
      .addMatcher(
        accountsListApi.endpoints.getAccountClosingBalances.matchFulfilled,
        (state, action: PayloadAction<{ data: Balance[] }>) => {
          if (state.account) {
            state.account = {
              ...state.account,
              closingBalances: action.payload.data,
            };
          }
        },
      )
      .addMatcher(
        accountsListApi.endpoints.closeAccount.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          state.account = action.payload.data;

          state.accounts = state?.accounts?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                overdraftEnabled: item.overdraftEnabled,
                search: '',
                url: generatePath(ROUTES.ACCOUNTS_EDIT, {
                  accountId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        },
      )
      .addMatcher(
        accountsListApi.endpoints.openAccount.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          state.account = action.payload.data;

          state.accounts = state?.accounts?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                overdraftEnabled: item.overdraftEnabled,
                search: '',
                url: generatePath(ROUTES.ACCOUNTS_EDIT, {
                  accountId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        },
      )
      .addMatcher(
        accountsListApi.endpoints.updateOverdraftStatus.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          state.account = {
            ...action.payload.data,
            closingBalances: state.account?.closingBalances,
          };

          state.accounts = state?.accounts?.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.overdraftEnabled = action.payload.data.overdraftEnabled;
            }

            return result;
          });
        },
      )
      .addMatcher(
        accountsListApi.endpoints.updateOverdraftBalanceValue.matchFulfilled,
        (state, action: PayloadAction<{ data: Balance }>) => {
          state.balances = state.balances?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });

          state.accounts = state?.accounts?.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.accountId) {
              if (result.balances) {
                const nextBalances = result.balances.map((balance) => {
                  let nextBalance = { ...balance };

                  if (nextBalance.id === action.payload.data.id) {
                    // @ts-ignore
                    nextBalance = action.payload.data;
                  }

                  return nextBalance;
                });

                result.balances = nextBalances;
              }
            }

            return result;
          });
        },
      )
      .addMatcher(
        accountsListApi.endpoints.blockDebit.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          state.account = action.payload.data;

          state.accounts = state?.accounts?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                overdraftEnabled: item.overdraftEnabled,
                search: '',
                url: generatePath(ROUTES.ACCOUNTS_EDIT, {
                  accountId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        },
      )
      .addMatcher(
        accountsListApi.endpoints.unblockDebit.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          state.account = action.payload.data;

          state.accounts = state?.accounts?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                overdraftEnabled: item.overdraftEnabled,
                search: '',
                url: generatePath(ROUTES.ACCOUNTS_EDIT, {
                  accountId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        },
      )
      .addMatcher(
        accountsListApi.endpoints.blockCredit.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          state.account = action.payload.data;

          state.accounts = state?.accounts?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                overdraftEnabled: item.overdraftEnabled,
                search: '',
                url: generatePath(ROUTES.ACCOUNTS_EDIT, {
                  accountId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        },
      )
      .addMatcher(
        accountsListApi.endpoints.unblockCredit.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          state.account = action.payload.data;

          state.accounts = state?.accounts?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                overdraftEnabled: item.overdraftEnabled,
                search: '',
                url: generatePath(ROUTES.ACCOUNTS_EDIT, {
                  accountId: action.payload.data.id,
                }),
              };
            }

            return result;
          });
        },
      )

      .addMatcher(
        accountsListApi.endpoints.getAccountBalances.matchFulfilled,
        (state, action: PayloadAction<Response<Balance>>) => {
          state.balances = action.payload.data.edges.map((item) => item.node);
        },
      )
      .addMatcher(
        accountsListApi.endpoints.updateAccount.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          const nextAccounts = state?.accounts?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                search: '',
                url: generatePath(ROUTES.ACCOUNTS_EDIT, {
                  accountId: action.payload.data.id,
                }),
              };
            }

            return result;
          });

          state.accounts = nextAccounts;
        },
      )
      .addMatcher(
        accountsListApi.endpoints.updateAccount.matchFulfilled,
        (state, action: PayloadAction<{ data: Account }>) => {
          state.account = action.payload.data;
        },
      );
  },
});

export const {
  clearAccountsState,
  setAccountsStateInitiate,
  clearAccoutState,
  clearAccountsClosingBalances,
} = accountsListSlice.actions;

export default accountsListSlice.reducer;
